import { forwardRef, memo, useRef } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../../lib/styles';
import { Loader } from '../Loader';
import { usePopoverTriggerContext } from '../Popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '../Tooltip';
import { useMergedRefs } from '@page/core/hooks/common';

const darkModeSharedStyles = [
  'dark:text-white dark:fill-white',
  'dark:bg-white/20 dark:hover:bg-white/30 dark:active:bg-white/40',
];

const buttonVariants = cva(
  'flex items-center gap-0.5 rounded-sm truncate transition-all data-[disabled=true]:cursor-not-allowed relative font-medium',
  {
    variants: {
      variant: {
        primary: cn(
          'text-white fill-white',
          'bg-bg-inter-primary-default border border-bg-inter-primary-default',
          'hover:bg-bg-inter-primary-hover hover:border-bg-inter-primary-hover',
          'focus:bg-bg-inter-primary-hover focus:border-bg-inter-primary-hover focus:outline-bg-inter-primary-hover',
          'active:bg-bg-inter-primary-pressed active:border-bg-inter-primary-pressed active:outline-bg-inter-primary-pressed',
          'data-[active=true]:bg-bg-inter-primary-hover data-[active=true]:border-bg-inter-primary-hover',
          'data-[disabled=true]:bg-gray-11 data-[disabled=true]:border-gray-11 data-[disabled=true]:text-gray-9 data-[disabled=true]:fill-gray-9',
          ...darkModeSharedStyles,
        ),
        secondary: cn(
          'text-text-primary fill-icon-primary',
          'bg-bg-inter-secondary-default border border-bg-inter-secondary-default',
          'hover:bg-bg-inter-secondary-hover hover:border-bg-inter-secondary-hover',
          'focus:bg-bg-inter-secondary-hover focus:border-bg-inter-secondary-hover',
          'active:border-bg-inter-secondary-pressed active:bg-bg-inter-secondary-pressed',
          'data-[disabled=true]:bg-bg-inter-secondary-default data-[disabled=true]:border-bg-inter-secondary-default data-[disabled=true]:text-text-disabled data-[disabled=true]:fill-icon-disabled',
          ...darkModeSharedStyles,
          'dark:border-[#3F3F3F]',
        ),
        tertiary: cn(
          'text-text-primary fill-icon-primary',
          'bg-bg-inter-tertiary-default border border-bg-inter-tertiary-default',
          'hover:bg-bg-inter-tertiary-hover',
          'focus:bg-bg-inter-tertiary-hover',
          'active:bg-bg-inter-tertiary-pressed',
          'data-[active=true]:bg-bg-inter-tertiary-hover data-[active=true]:border-border-input-select',
          'data-[disabled=true]:bg-transparent data-[disabled=true]:text-text-disabled data-[disabled=true]:fill-icon-disabled',
        ),
        outline: cn(
          'text-text-primary fill-icon-primary',
          'bg-bg-sur-primary hover:bg-bg-inter-tertiary-hover active:bg-bg-inter-tertiary-pressed `active`:bg-bg-inter-tertiary-pressed',
          'border border-border-default',
          'data-[disabled=true]:bg-transparent data-[disabled=true]:border-border-default data-[disabled=true]:text-text-disabled data-[disabled=true]:fill-icon-disabled',
          ...darkModeSharedStyles,
          'dark:border-[#3F3F3F]',
        ),
        link: cn(
          'text-text-primary fill-icon-primary font-semibold',
          'bg-transparent hover:underline',
          'data-[disabled=true]:opacity-50',
        ),
      },
      active: {
        true: '',
        false: '',
      },
      activeVariant: {
        default: '',
        highlight: '',
        background: '',
      },
      size: {
        lg: 'h-10 px-4 font-body-md',
        md: 'h-8 px-3 font-body-sm',
        sm: 'h-7 px-2.5 font-body-sm',
        xs: 'h-6 px-2 font-body-xs',
      },
      color: {
        default: '',
        alert: cn(
          'text-text-primary fill-text-primary',
          'bg-orange-3 hover:bg-orange-4 focus:bg-orange-4 active:bg-orange-4',
          'border border-orange-3 hover:border-orange-4 focus:border-orange-4 active:border-orange-4',
        ),
        info: cn(
          'text-text-primary fill-text-primary',
          'bg-blue-2 hover:bg-blue-3 focus:bg-blue-3 active:bg-blue-3',
          'border border-blue-2 hover:border-blue-3 focus:border-blue-3 active:border-blue-3',
        ),
        destructive: '',
      },
      /**
       * overrideStyles is needed for `a`-based buttons when global link resets (e.g. `a { background: transparent }`)
       * beat component styles; uses `!important` to win specificity.
       */
      overrideStyles: {
        true: '',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      color: 'default',
      overrideStyles: false,
      activeVariant: 'default',
    },
    compoundVariants: [
      {
        variant: 'secondary',
        color: 'destructive',
        className: cn(
          'text-text-destructive fill-text-destructive',
          'bg-bg-inter-destructive-hover hover:bg-bg-inter-destructive-pressed focus:bg-bg-inter-destructive-pressed active:bg-bg-inter-destructive-pressed',
          'border border-bg-inter-destructive-hover hover:border-bg-inter-destructive-pressed focus:border-bg-inter-destructive-pressed active:border-bg-inter-destructive-pressed',
        ),
      },
      {
        variant: 'tertiary',
        color: 'destructive',
        className: cn(
          'text-text-destructive fill-text-destructive',
          'hover:bg-bg-inter-destructive-hover focus:bg-bg-inter-destructive-hover active:bg-bg-inter-destructive-pressed',
          'hover:border-bg-inter-destructive-hover focus:border-bg-inter-destructive-hover active:border-bg-inter-destructive-pressed',
        ),
      },
      {
        variant: 'link',
        size: 'sm',
        className: cn(
          'px-0 h-6',
          'data-[active=true]:text-icon-link data-[active=true]:fill-icon-link',
          'data-[active=true]:bg-bg-inter-tertiary-active data-[active=true]:px-2',
        ),
      },
      {
        overrideStyles: true,
        variant: 'primary',
        className: cn(
          'bg-bg-inter-primary-default!',
          'hover:bg-bg-inter-primary-hover!',
          'focus:bg-bg-inter-primary-hover!',
          'active:bg-bg-inter-primary-pressed!',
          'data-[active=true]:bg-bg-inter-primary-hover!',
          'data-[disabled=true]:bg-gray-11!',
          'dark:bg-white/20! dark:hover:bg-white/30! dark:active:bg-white/40!',
        ),
      },
      {
        variant: 'secondary',
        activeVariant: 'default',
        className: cn('data-[active=true]:bg-bg-inter-secondary-hover data-[active=true]:border-border-input-select'),
      },
      {
        variant: 'secondary',
        activeVariant: 'highlight',
        className: cn(
          'data-[active=true]:bg-bg-inter-tertiary-active data-[active=true]:border-bg-inter-tertiary-active',
        ),
      },
      {
        variant: 'tertiary',
        activeVariant: 'background',
        className: cn(
          'data-[active=true]:bg-blue-3 data-[active=true]:border-blue-3',
          'data-[active=true]:hover:bg-blue-3/80 data-[active=true]:active:bg-blue-3 data-[active=true]:active:outline-blue-2',
        ),
      },
    ],
  },
);

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement> & React.AnchorHTMLAttributes<HTMLAnchorElement>, 'color'>,
    VariantProps<typeof buttonVariants> {
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  asChild?: boolean;
  loading?: boolean;
  active?: boolean;
  element?: React.ElementType;
  contentClassName?: string;
  /**
   * Shown on hover and focus, portalled so no container clips or covers it. On a disabled button it
   * still shows — the place to say why the button is disabled.
   */
  tooltip?: React.ReactNode;
  tooltipProps?: Pick<React.ComponentProps<typeof TooltipContent>, 'side' | 'align' | 'className' | 'withinPortal'>;
}

export type ButtonSize = VariantProps<typeof buttonVariants>['size'];

const ButtonBase = (
  {
    children,
    className,
    variant,
    size,
    color,
    disabled = false,
    leftIcon,
    rightIcon,
    asChild = false,
    loading = false,
    active: activeProp,
    activeVariant,
    element = 'button',
    type = 'button',
    overrideStyles = false,
    contentClassName,
    tooltip,
    tooltipProps,
    onClick,
    ...rest
  }: ButtonProps,
  forwardedRef: React.Ref<HTMLButtonElement>,
) => {
  const internalRef = useRef<HTMLButtonElement | null>(null);
  const ref = useMergedRefs<HTMLButtonElement>(forwardedRef, internalRef);
  const { open: isPopoverOpen } = usePopoverTriggerContext(internalRef);

  const active = activeProp ?? isPopoverOpen ?? false;
  const Comp: React.ElementType = asChild ? Slot : element;
  const isMd = size === 'md' || !size;
  // A disabled button with a tooltip has to stay hoverable and focusable for the tooltip to open, so
  // it is disabled through aria-disabled and swallows its own activation. Natively disabled, it would
  // take no pointer events and no focus, and the reason would never show.
  const isDisabledWithTooltip = Boolean(disabled && tooltip);

  const content = (
    <Comp
      ref={ref}
      className={cn(
        buttonVariants({ variant, size, color, active, overrideStyles, activeVariant }),
        {
          'cursor-pointer': !disabled && !loading,
          'pointer-events-none': loading || (disabled && !isDisabledWithTooltip),
        },
        { 'pl-2': !!leftIcon && isMd, 'pr-2': !!rightIcon && isMd },
        className,
      )}
      disabled={disabled && !isDisabledWithTooltip}
      aria-disabled={isDisabledWithTooltip || undefined}
      data-disabled={disabled}
      aria-busy={loading || undefined}
      type={type}
      onClick={
        isDisabledWithTooltip
          ? (event: React.MouseEvent) => {
              // Also keeps a submit button from submitting its form.
              event.preventDefault();
            }
          : onClick
      }
      {...(active ? { 'data-active': true } : {})}
      {...rest}
    >
      {loading && <Loader className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" inverted />}

      {/* opacity-0 (not `invisible`) keeps the label in the a11y tree so the button keeps an
          accessible name while loading; pointer-events-none still blocks interaction. */}
      <div
        data-slot="content"
        className={cn(
          'flex items-center justify-center gap-0.5 w-full',
          { 'opacity-0 pointer-events-none': loading },
          contentClassName,
        )}
      >
        {leftIcon}
        {children}
        {rightIcon}
      </div>
    </Comp>
  );

  if (!tooltip) return content;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{content}</TooltipTrigger>
      <TooltipContent withinPortal {...tooltipProps}>
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
};

export const Button = memo(forwardRef(ButtonBase));

export { buttonVariants };
